import DOMPurify from 'dompurify';
import linkifyHtml from 'linkify-html';

// R39 (2026-09-07): links in displayed HTML always open in a new tab. The
// editor no longer stamps target=_blank on links it stores (see
// RichTextEditor), so the display layer owns that rule for old and new
// content alike. In-page anchors (#) and javascript: are left alone/removed.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName !== 'A') return;
  const href = node.getAttribute('href') || '';
  if (!href || href.startsWith('#')) return;
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
});

// Final review 2026-09-07 (comms P2-1): posts are HTML from any member, on web
// or phone, and the server does not sanitise. Beyond DOMPurify's defaults we
// refuse form controls (fake login forms), inline styles that can cover the
// page (position/z-index/inset), and iframes to anything but the video hosts
// the editor can embed; every iframe gets a fixed `allow` list (no camera or
// microphone delegation) and a sandbox.
const EMBED_HOSTS = new Set([
  'www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com',
  'www.loom.com', 'loom.com', 'player.vimeo.com', 'vimeo.com',
]);
export const IFRAME_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
export const IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation';
// Admin-authored training content (item 5, 2026-09-16) may embed anything on
// https — Google Forms, Drive/Docs previews, publit.io, SoundCloud. Forms POST
// their answers, which a sandbox without `allow-forms` silently blocks.
export const IFRAME_SANDBOX_TRUSTED = `${IFRAME_SANDBOX} allow-forms`;
const RISKY_STYLE = /(^|;)\s*(position|z-index|inset|top|left|right|bottom|transform|pointer-events)\s*:/i;
export const embedHostAllowed = (src) => {
  try {
    const u = new URL(src);
    return u.protocol === 'https:' && EMBED_HOSTS.has(u.hostname.toLowerCase());
  } catch { return false; }
};
// An iframe pointing back at the app itself would run the app inside the app
// with `allow-scripts allow-same-origin` — same-origin script access to our own
// session. Nothing legitimate embeds us in us, in either tier.
const isOwnOrigin = (u) => (
  typeof window !== 'undefined' && !!window.location && u.origin === window.location.origin
);
const httpsSrc = (src) => {
  try {
    const u = new URL(src);
    return u.protocol === 'https:' && !isOwnOrigin(u);
  } catch { return false; }
};

// Trust tier for the CURRENT DOMPurify.sanitize() call. DOMPurify's hooks are
// global and the sanitize pass is synchronous, so a module-level flag set
// immediately before the call (and reset in `finally`) is the supported way to
// vary hook behaviour per call. Strict (false) is the default for every caller.
let trustedEmbeds = false;
const iframeSrcAllowed = (src) => {
  let u;
  try { u = new URL(src); } catch { return false; }
  if (isOwnOrigin(u)) return false; // both tiers, however the host list changes
  return trustedEmbeds ? httpsSrc(src) : embedHostAllowed(src);
};

DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'style' && RISKY_STYLE.test(data.attrValue || '')) { data.keepAttr = false; return; }
  if (node.nodeName !== 'IFRAME') return;
  if (data.attrName === 'src' && !iframeSrcAllowed(data.attrValue || '')) data.keepAttr = false;
  if (data.attrName === 'allow') data.attrValue = IFRAME_ALLOW;
  if (data.attrName === 'srcdoc' || data.attrName === 'sandbox') data.keepAttr = false;
});
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'IFRAME' && !iframeSrcAllowed(node.getAttribute('src') || '') && node.parentNode) {
    node.parentNode.removeChild(node);
  }
});

// Non-video embeds (forms, docs, PDFs) are tall, not 16:9. The render surfaces
// force `[&_iframe]:aspect-video` on every iframe, so give these an inline
// height (which beats the utility class) and neutralise the ratio.
const DEFAULT_EMBED_HEIGHT = 600;
const embedHeightStyle = (heightAttr) => {
  const n = /^\d+$/.test((heightAttr || '').trim()) ? parseInt(heightAttr, 10) : NaN;
  const h = n >= 100 && n <= 4000 ? n : DEFAULT_EMBED_HEIGHT;
  return `height:${h}px;aspect-ratio:auto;`;
};

/**
 * Style @mentions in text/HTML using the mentions metadata array.
 * Replaces @Name with a styled span for each mention.
 */
/**
 * Style @mentions in text/HTML using the mentions metadata array.
 * Replaces @Name with a styled span for each mention.
 *
 * Mention entry shapes:
 *   - { user_id, name }              -> matched on `name`
 *   - { group_id, tag, name }        -> matched on `tag` (the @-text in the body)
 */
export function applyMentionStyling(text, mentions) {
  if (!text || !mentions?.length) return text;
  let result = text;
  const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // Sort by match-length descending so longer names match first
  // (prevents "@John" from partially matching inside "@John Smith"
  //  and "@worship-team" from partial-matching as "@worship")
  const matchTextOf = m => m.tag || m.name;
  const sorted = [...mentions].sort((a, b) => (matchTextOf(b)?.length || 0) - (matchTextOf(a)?.length || 0));
  sorted.forEach(m => {
    const matchText = matchTextOf(m);
    if (!matchText) return;
    const escaped = matchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cls = m.group_id ? 'mention-tag mention-group' : 'mention-tag';
    result = result.replace(
      new RegExp(`@${escaped}`, 'g'),
      `<span class="${cls}">@${esc(matchText)}</span>`
    );
  });
  return result;
}

/**
 * Escape HTML special characters in plain text (for rendering user input safely via innerHTML).
 */
export function escapeHtml(text) {
  if (!text) return '';
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Sanitize HTML to prevent XSS attacks.
 * Bare URLs are auto-linked before sanitization so they render as clickable <a> tags.
 * Allows standard formatting tags, links, images, and iframes (for embeds).
 *
 * @param {string} html
 * @param {{trustedEmbeds?: boolean}} [options] `trustedEmbeds` is for
 *   ADMIN-AUTHORED TRAINING CONTENT ONLY (the training library / candidate
 *   lesson pages and their admin previews), never for member-authored posts,
 *   DMs, announcements or intake docs. It widens iframes to ANY https source
 *   and adds `allow-forms` to the sandbox so an embedded Google Form can
 *   submit. Everything else stays identical: no `srcdoc`, author `sandbox`
 *   dropped, `allow` and `referrerpolicy` forced, YouTube → nocookie.
 */
export function sanitizeHtml(html, options) {
  if (!html) return '';
  const trusted = !!(options && options.trustedEmbeds);
  const linked = linkifyHtml(html, { target: '_blank', rel: 'noopener noreferrer' });
  let clean;
  trustedEmbeds = trusted;
  try {
    clean = DOMPurify.sanitize(linked, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'target', 'rel', 'referrerpolicy'],
      FORBID_TAGS: ['form', 'input', 'textarea', 'select', 'button', 'style', 'base', 'meta', 'link', 'object', 'embed'],
    });
  } finally {
    trustedEmbeds = false;
  }
  // YouTube embeds require a Referer header or they fail with error 153
  // (notably in the desktop app webview). Force the policy on all iframes.
  if (clean.includes('<iframe')) {
    const doc = new DOMParser().parseFromString(clean, 'text/html');
    doc.querySelectorAll('iframe').forEach(f => {
      f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      f.setAttribute('allow', IFRAME_ALLOW);
      f.setAttribute('sandbox', trusted ? IFRAME_SANDBOX_TRUSTED : IFRAME_SANDBOX);
      // nocookie domain avoids YouTube error 153 in embedded webviews
      const src = f.getAttribute('src') || '';
      if (src.includes('youtube.com/embed/')) {
        f.setAttribute('src', src.replace('youtube.com/embed/', 'youtube-nocookie.com/embed/'));
      } else if (trusted && !embedHostAllowed(src)
                 && !/aspect-ratio/i.test(f.getAttribute('style') || '')) {
        // A form/doc/PDF/audio-player embed: honour its own height instead of
        // the surface's 16:9. Embeds that declare their OWN aspect-ratio (the
        // publit.io defaults, and anything `embed_iframe_html` generated) are
        // left alone so they keep rendering exactly as they always have.
        f.setAttribute('style', embedHeightStyle(f.getAttribute('height')));
      }
    });
    return doc.body.innerHTML;
  }
  return clean;
}

/**
 * Turn the "spaces that are not spaces" rich-text paste produces into ordinary
 * spaces so text wraps at words: non-breaking spaces (U+00A0 / &nbsp;, what
 * Apple Mail, Notes and Word emit) and zero-width no-break spaces (U+FEFF,
 * U+2060). Only runs between two non-space characters, so deliberate double
 * spacing (TipTap stores the second space as nbsp) keeps its one real space.
 * Used at display time for existing posts and at paste time in the editor.
 */
export function normalizePastedSpaces(html) {
  if (!html || typeof html !== 'string') return html || '';
  return html
    .replace(/[\u2060\uFEFF]/g, '')
    .replace(/(\S)(?:\u00a0|&nbsp;)(?=\S)/g, '$1 '); // no lookbehind: Safari/WebKit < 16.4 cannot parse it
}
